/**
 * AuthGate.jsx — ELYON-SHELL-FOUNDATION-1
 *
 * Firebase Auth wrapper. Uses onAuthStateChanged (no polling).
 * - Unauthenticated: render branded login screen.
 * - Authenticated: render children.
 * - Firebase not configured (local dev): pass-through with banner.
 */
import { useState, useEffect } from 'react';
import { auth, isConfigured } from '../firebase/config.js';

/**
 * @param {{ children: import('react').ReactNode }} props
 */
export function AuthGate({ children }) {
  const [user, setUser]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [signInEmail, setSignInEmail] = useState('');
  const [signInPassword, setSignInPassword] = useState('');
  const [signInError, setSignInError] = useState('');
  const [signInLoading, setSignInLoading] = useState(false);

  useEffect(() => {
    // If Firebase is not configured (local dev without .env), pass through.
    if (!isConfigured || !auth) {
      setLoading(false);
      setUser({ uid: 'dev-local', email: 'dev@local', displayName: 'Dev Local' });
      return;
    }

    const { onAuthStateChanged } = /** @type {any} */ (auth);
    // Firebase v12 auth uses the modular API
    import('firebase/auth').then(({ onAuthStateChanged: listenFn }) => {
      const unsubscribe = listenFn(auth, (firebaseUser) => {
        setUser(firebaseUser);
        setLoading(false);
      });
      return () => unsubscribe();
    });
  }, []);

  async function handleSignIn(e) {
    e.preventDefault();
    setSignInError('');
    setSignInLoading(true);
    try {
      const { signInWithEmailAndPassword } = await import('firebase/auth');
      await signInWithEmailAndPassword(auth, signInEmail, signInPassword);
    } catch (err) {
      setSignInError(err.message ?? 'Sign-in failed. Please try again.');
    } finally {
      setSignInLoading(false);
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="elyon-auth-gate" aria-busy="true" aria-label="Authenticating…">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'var(--space-4)',
        }}>
          <div className="elyon-auth-wordmark">ELYON</div>
          <div style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--font-size-sm)' }}>
            Authenticating…
          </div>
        </div>
      </div>
    );
  }

  // Authenticated — render the app shell
  if (user) return <>{children}</>;

  // Unauthenticated — render branded login
  return (
    <div className="elyon-auth-gate">
      <div
        className="elyon-auth-card"
        role="main"
        aria-label="Sign in to Elyon Platform"
      >
        <div className="elyon-auth-wordmark" aria-label="Elyon Platform">
          ELYON
        </div>

        <div>
          <h1 className="elyon-auth-title">Sign in</h1>
          <p className="elyon-auth-subtitle">Access the Elyon Platform</p>
        </div>

        <form
          onSubmit={handleSignIn}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3)',
            width: '100%',
          }}
          aria-label="Sign-in form"
          noValidate
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
            <span style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--ink-navy)', WebkitTextFillColor: 'var(--ink-navy)',
            }}>
              Email
            </span>
            <input
              type="email"
              value={signInEmail}
              onChange={(e) => setSignInEmail(e.target.value)}
              required
              autoComplete="email"
              aria-required="true"
              style={{
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--color-border-default)',
                background: 'var(--white)',
                color: 'var(--ink-navy)', WebkitTextFillColor: 'var(--ink-navy)',
                fontFamily: 'var(--font-family-body)',
                fontSize: 'var(--font-size-sm)',
              }}
            />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
            <span style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--ink-navy)', WebkitTextFillColor: 'var(--ink-navy)',
            }}>
              Password
            </span>
            <input
              type="password"
              value={signInPassword}
              onChange={(e) => setSignInPassword(e.target.value)}
              required
              autoComplete="current-password"
              aria-required="true"
              style={{
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--color-border-default)',
                background: 'var(--white)',
                color: 'var(--ink-navy)', WebkitTextFillColor: 'var(--ink-navy)',
                fontFamily: 'var(--font-family-body)',
                fontSize: 'var(--font-size-sm)',
              }}
            />
          </label>

          {signInError && (
            <div className="elyon-auth-error" role="alert">
              {signInError}
            </div>
          )}

          <button
            type="submit"
            className="elyon-auth-btn"
            disabled={signInLoading}
            aria-busy={signInLoading}
          >
            {signInLoading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-tertiary)',
          textAlign: 'center',
          margin: 0,
        }}>
          Elyon Platform · Powered by Elyon SCI, LLC
        </p>
      </div>
    </div>
  );
}
